import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "../../lib/api";


import { ArticleCard } from "./ResearchHub";
import { Search } from "lucide-react";

const CATEGORY_META = {
  "findings":     { label: "Findings",         heading: "Findings",          tagline: "Short research observations and discoveries." },
  "teardowns":    { label: "Teardowns",        heading: "Teardowns",         tagline: "Comparative analysis between companies." },
  "reports":      { label: "Industry Reports", heading: "Industry Reports",  tagline: "Long-form research publications." },
  "case-studies": { label: "Case Studies",     heading: "Case Studies",      tagline: "Audit outcomes and score improvements." },
  "resources":    { label: "Resources",        heading: "Resources",         tagline: "Evergreen explainers and educational content." },
};

export default function ResearchCategory() {
  const { category } = useParams();
  const meta = CATEGORY_META[category];

  const [items, setItems] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const industriesLoadedRef = useRef(false);

  const fetchData = useMemo(() => async () => {
    setLoading(true);
    try {
      const params = { category, page_size: 30 };
      if (q.trim()) params.q = q.trim();
      if (industry) params.industry = industry;
      if (featuredOnly) params.featured = true;
      const itemsReq = api.get("/research/publications", { params });
      const industriesReq = industriesLoadedRef.current
        ? Promise.resolve(null)
        : api.get("/research/industries");
      const [itemsRes, industriesRes] = await Promise.all([itemsReq, industriesReq]);
      setItems(itemsRes.data.items || []);
      if (industriesRes) {
        setIndustries(industriesRes.data.industries || []);
        industriesLoadedRef.current = true;
      }
    } finally {
      setLoading(false);
    }
  }, [category, q, industry, featuredOnly]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!meta) {
    return (
      <div className="min-h-screen bg-[#080807] text-[#f4efe6]">
  
        <main className="max-w-5xl mx-auto px-6 sm:px-8 py-24">
          <h1 className="font-serif-display text-3xl">Unknown research section</h1>
          <Link to="/research" className="text-[#b8963e] hover:underline">← Back to Research</Link>
        </main>
  
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080807] text-[#f4efe6]" data-testid={`research-listing-${category}`}>
      <Helmet>
        <title>{`${meta.heading} · The AI Visibility Index™`}</title>
        <meta name="description" content={meta.tagline} />
        <link rel="canonical" href={`https://theentityagency.com/research/${category}`} />
      </Helmet>


      <section className="border-b border-[#2a2620]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
          <nav className="font-mono-eav text-[11px] tracking-[0.2em] uppercase text-[#8a7e6d]"
               aria-label="Breadcrumb">
            <Link to="/research" className="hover:text-[#b8963e]">Research</Link>
            <span className="mx-2">/</span>
            <span className="text-[#b8963e]">{meta.label}</span>
          </nav>
          <h1 className="mt-4 font-serif-display text-5xl sm:text-6xl text-[#f4efe6] leading-tight"
              style={{ fontWeight: 500 }}>
            {meta.heading}
          </h1>
          <p className="mt-4 text-lg text-[#c9bca4] max-w-2xl">{meta.tagline}</p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-[#2a2620]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-6 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a7e6d]" />
            <input
              type="text"
              data-testid="research-search-input"
              placeholder="Search publications…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="eav-input pl-10 w-full"
            />
          </div>
          <select
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            data-testid="research-industry-filter"
            className="eav-input"
          >
            <option value="">All industries</option>
            {industries.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
          <label className="inline-flex items-center gap-2 text-sm text-[#c9bca4] cursor-pointer"
                 data-testid="research-featured-filter">
            <input type="checkbox" checked={featuredOnly} onChange={(e) => setFeaturedOnly(e.target.checked)}
                   className="accent-[#b8963e]" />
            Featured only
          </label>
        </div>
      </section>

      {/* Grid */}
      <section>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-12">
          {loading ? (
            <p className="text-[#8a7e6d]">Loading…</p>
          ) : items.length === 0 ? (
            <div className="py-16 text-center" data-testid="research-empty-state">
              <p className="font-mono-eav text-xs tracking-[0.22em] uppercase text-[#8a7e6d]">No matches</p>
              <p className="mt-2 text-[#c9bca4]">Try clearing filters or check back soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((it) => <ArticleCard key={it.id} item={it} />)}
            </div>
          )}
        </div>
      </section>


    </div>
  );
}
